import { Slot, Slottable } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '../../utils/cn'

const badgeVariants = cva(
  'inline-flex items-center justify-center rounded-full border font-normal normal-nums leading-none w-fit whitespace-nowrap shrink-0 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden',
  {
    variants: {
      variant: {
        default: 'border-transparent bg-primary text-primary-foreground [a&]:hover:bg-primary/90',
        secondary: 'border-transparent bg-muted text-foreground [a&]:hover:bg-muted/90',
        raised:
          'border-border bg-card border-[0.5px] text-foreground shadow-sm dark:border-transparent dark:bg-muted dark:shadow-none',
        tertiary: 'border-transparent bg-muted text-secondary-foreground [a&]:hover:bg-muted/90',
        destructive:
          'border-transparent bg-destructive text-destructive-foreground [a&]:hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive',
        success: 'border-transparent bg-success text-success-foreground [a&]:hover:bg-success/90',
        warning: 'border-transparent bg-warning text-warning-foreground [a&]:hover:bg-warning/90',
        outline: 'text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground',
        info: 'text-info-foreground bg-info border-transparent',
        'success-outline': 'border-border text-success-foreground [a&]:hover:bg-success',
        'warning-outline': 'border-border text-warning-foreground [a&]:hover:bg-warning',
        'destructive-outline':
          'border-border text-destructive-foreground [a&]:hover:bg-destructive focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40',
        'info-outline': 'border-border text-info-foreground [a&]:hover:bg-info',
        'secondary-outline': 'border-border text-muted-foreground [a&]:hover:bg-muted'
      },
      size: {
        sm: 'h-5 px-1.5 text-4xs gap-1 [&>svg]:size-3 [&>[data-slot=avatar]]:size-3',
        default: 'h-6 px-2 text-xs gap-1 [&>svg]:size-3 [&>[data-slot=avatar]]:size-3',
        lg: 'h-7 px-2.5 text-xs gap-1.5 [&>svg]:size-3.5 [&>[data-slot=avatar]]:size-3.5'
      }
    },
    defaultVariants: {
      variant: 'default',
      size: 'default'
    }
  }
)

export type BadgeProps = React.ComponentProps<'span'> &
  VariantProps<typeof badgeVariants> & {
    asChild?: boolean
    /** Leading status dot that inherits the variant's text color via `bg-current`. */
    dot?: boolean
  }

function Badge({
  className,
  variant,
  size,
  asChild = false,
  dot = false,
  children,
  ...props
}: BadgeProps) {
  const Comp = asChild ? Slot : 'span'

  return (
    <Comp data-slot="badge" className={cn(badgeVariants({ variant, size }), className)} {...props}>
      {dot && <span aria-hidden className="size-1.5 shrink-0 rounded-full bg-current" />}
      <Slottable>{children}</Slottable>
    </Comp>
  )
}

export { Badge, badgeVariants }
